/**
 * FASE 1B - Impostazioni Gestione Economica
 * Configurazione commissioni, cedolare secca, costi transazione
 * + Dati Aziendali per Ricevute Fiscali
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, Save, RotateCcw, Percent, Calculator, 
  Building, CreditCard, FileText, Info, Check, Upload, 
  Image, Trash2, Phone, Mail, Globe, MapPin, User,
  Calendar, Plus, Edit2, X, Users, Euro
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Componente per gestione Tassa di Soggiorno
function TouristTaxSettings({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    iva_percent: 10,
    tariffs: [],
    booking_includes_tax: true,
    apply_to_channels: ['booking.com', 'Booking.com', 'booking'],
    notes: ''
  });
  const [editingTariff, setEditingTariff] = useState(null);
  const [newTariff, setNewTariff] = useState({
    name: '',
    date_from: '',
    date_to: '',
    amount_per_person: 1.0,
    max_nights: 7
  });

  useEffect(() => {
    fetchTouristTaxSettings();
  }, [token]);

  const fetchTouristTaxSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/economic/settings/tourist-tax`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching tourist tax settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/economic/settings/tourist-tax`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni tassa di soggiorno salvate!');
    } catch (error) {
      console.error('Error saving tourist tax settings:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTariff = () => {
    if (!newTariff.name || !newTariff.date_from || !newTariff.date_to) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    const tariff = {
      id: Date.now().toString(),
      ...newTariff,
      amount_per_person: parseFloat(newTariff.amount_per_person),
      max_nights: parseInt(newTariff.max_nights)
    };
    
    setSettings(prev => ({
      ...prev,
      tariffs: [...prev.tariffs, tariff]
    }));
    
    setNewTariff({
      name: '',
      date_from: '',
      date_to: '',
      amount_per_person: 1.0,
      max_nights: 7
    });
    
    toast.success('Tariffa aggiunta! Ricorda di salvare.');
  };

  const handleDeleteTariff = (tariffId) => {
    setSettings(prev => ({
      ...prev,
      tariffs: prev.tariffs.filter(t => t.id !== tariffId)
    }));
    toast.success('Tariffa rimossa! Ricorda di salvare.');
  };

  const handleUpdateTariff = (tariffId, field, value) => {
    setSettings(prev => ({
      ...prev,
      tariffs: prev.tariffs.map(t => 
        t.id === tariffId ? { ...t, [field]: value } : t
      )
    }));
  };

  if (loading) {
    return (
      <Card className="border-cyan-200">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-cyan-200">
      <CardHeader className="pb-2 bg-cyan-50 rounded-t-lg">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Euro className="w-4 h-4 text-cyan-600" />
              Tassa di Soggiorno
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Configura le tariffe per periodo. La tassa viene scorporata dalle prenotazioni Booking (solo adulti).
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-cyan-600 hover:bg-cyan-700">
            {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <Save className="w-4 h-4 mr-1" />}
            Salva
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-6">
        
        {/* Impostazioni generali */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Booking include tassa?</Label>
            <p className="text-xs text-gray-400 mb-1">Scorporare da importo Booking</p>
            <select
              value={settings.booking_includes_tax ? 'yes' : 'no'}
              onChange={(e) => setSettings(prev => ({ ...prev, booking_includes_tax: e.target.value === 'yes' }))}
              className="w-full border rounded-md p-2 text-sm"
            >
              <option value="yes">Sì, scorporare</option>
              <option value="no">No, non inclusa</option>
            </select>
          </div>

          <div>
            <Label className="text-sm">Notti max tassabili</Label>
            <p className="text-xs text-gray-400 mb-1">Soglia legale (7 default)</p>
            <Input
              type="number"
              min="1"
              max="30"
              value={settings.tariffs[0]?.max_nights || 7}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 7;
                setSettings(prev => ({
                  ...prev,
                  tariffs: prev.tariffs.map(t => ({ ...t, max_nights: val }))
                }));
              }}
              className="w-20"
            />
          </div>
        </div>

        {/* Tariffe per periodo */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-cyan-600" />
              Tariffe per Periodo
            </Label>
          </div>

          {/* Lista tariffe esistenti */}
          <div className="space-y-2 mb-4">
            {settings.tariffs.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-lg">
                Nessuna tariffa configurata. Aggiungi le tariffe per i diversi periodi.
              </p>
            ) : (
              settings.tariffs.map((tariff) => (
                <div key={tariff.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-gray-400">Periodo</span>
                      <p className="font-medium">{tariff.name}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Dal</span>
                      <p>{tariff.date_from}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">Al</span>
                      <p>{tariff.date_to}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400">€/persona/notte</span>
                      <p className="font-bold text-cyan-600">€{tariff.amount_per_person.toFixed(2)}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDeleteTariff(tariff.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Form nuova tariffa */}
          <div className="p-4 border-2 border-dashed border-cyan-200 rounded-lg bg-cyan-50/50">
            <p className="text-sm font-medium text-cyan-700 mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Aggiungi Nuova Tariffa
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Nome Periodo</Label>
                <Input
                  placeholder="Es: Alta Stagione"
                  value={newTariff.name}
                  onChange={(e) => setNewTariff(prev => ({ ...prev, name: e.target.value }))}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Dal</Label>
                <Input
                  type="date"
                  value={newTariff.date_from}
                  onChange={(e) => setNewTariff(prev => ({ ...prev, date_from: e.target.value }))}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input
                  type="date"
                  value={newTariff.date_to}
                  onChange={(e) => setNewTariff(prev => ({ ...prev, date_to: e.target.value }))}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">€/persona/notte</Label>
                <Input
                  type="number"
                  step="0.10"
                  min="0"
                  value={newTariff.amount_per_person}
                  onChange={(e) => setNewTariff(prev => ({ ...prev, amount_per_person: e.target.value }))}
                  className="text-sm"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleAddTariff} className="w-full bg-cyan-600 hover:bg-cyan-700">
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Nota:</strong> La tassa di soggiorno viene calcolata solo sugli <strong>adulti</strong> (i bambini sono esenti). 
              Per le prenotazioni Booking.com, l&apos;importo viene automaticamente scorporato dal totale.
              Le notti oltre il limite massimo ({settings.tariffs[0]?.max_nights || 7}) non vengono tassate.
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EconomicSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    // Commissioni e Tasse
    commissione_booking: 15.0,
    commissione_airbnb: 3.0,
    commissione_expedia: 15.0,
    commissione_vrbo: 8.0,
    commissione_direct: 0.0,
    costo_transazione: 1.5,
    cedolare_secca: 21.0,
    iva_percentuale: 0.0,
    altre_deduzioni: 0.0,
    // Dati Aziendali per Ricevute Fiscali
    azienda_nome: '',
    azienda_sottotitolo: '',
    azienda_indirizzo: '',
    azienda_cap: '',
    azienda_citta: '',
    azienda_provincia: '',
    azienda_codice_fiscale: '',
    azienda_partita_iva: '',
    azienda_telefono: '',
    azienda_email: '',
    azienda_sito_web: '',
    azienda_logo_url: '',
    // Template Email Fatture
    email_fattura_oggetto: 'Ricevuta Fiscale nr. {{numero}} - {{azienda_nome}}',
    email_fattura_corpo: `Gentile {{cliente_nome}},

in allegato trova la ricevuta fiscale nr. {{numero}} relativa al suo soggiorno.

Importo totale: {{totale}}
{{#if acconto}}Acconto versato: {{acconto}} - Saldo: {{saldo}}{{/if}}

Per qualsiasi chiarimento non esiti a contattarci.

Cordiali saluti,
{{azienda_nome}}
Tel: {{azienda_telefono}}
{{azienda_email}}`,
    email_fattura_firma: ''
  });

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/financial/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/financial/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      commissione_booking: 15.0,
      commissione_airbnb: 3.0,
      commissione_expedia: 15.0,
      commissione_vrbo: 8.0,
      commissione_direct: 0.0,
      costo_transazione: 1.5,
      cedolare_secca: 21.0,
      iva_percentuale: 0.0,
      altre_deduzioni: 0.0,
      // Mantieni i dati aziendali durante il reset delle commissioni
      azienda_nome: settings.azienda_nome,
      azienda_sottotitolo: settings.azienda_sottotitolo,
      azienda_indirizzo: settings.azienda_indirizzo,
      azienda_cap: settings.azienda_cap,
      azienda_citta: settings.azienda_citta,
      azienda_provincia: settings.azienda_provincia,
      azienda_codice_fiscale: settings.azienda_codice_fiscale,
      azienda_partita_iva: settings.azienda_partita_iva,
      azienda_telefono: settings.azienda_telefono,
      azienda_email: settings.azienda_email,
      azienda_sito_web: settings.azienda_sito_web,
      azienda_logo_url: settings.azienda_logo_url
    });
  };

  const handleChange = (field, value) => {
    // Campi numerici vs campi testo
    const numericFields = [
      'commissione_booking', 'commissione_airbnb', 'commissione_expedia',
      'commissione_vrbo', 'commissione_direct', 'costo_transazione',
      'cedolare_secca', 'iva_percentuale', 'iva_percent', 'altre_deduzioni'
    ];
    
    setSettings(prev => ({
      ...prev,
      [field]: numericFields.includes(field) ? (parseFloat(value) || 0) : value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo file
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine (PNG, JPG, WebP)');
      return;
    }
    
    // Verifica dimensione (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Il file è troppo grande (max 2MB)');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'logo');
      
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.url) {
        setSettings(prev => ({ ...prev, azienda_logo_url: response.data.url }));
        toast.success('Logo caricato!');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Errore nel caricamento del logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, azienda_logo_url: '' }));
  };

  // Calcolo esempio prenotazione €200
  const exampleLordo = 200;
  const exampleCommOTA = exampleLordo * (settings.commissione_booking / 100);
  const exampleTransazione = exampleLordo * (settings.costo_transazione / 100);
  const examplePreTax = exampleLordo - exampleCommOTA - exampleTransazione;
  const exampleCedolare = examplePreTax * (settings.cedolare_secca / 100);
  const exampleNetto = examplePreTax - exampleCedolare;

  if (loading) {
    return (
      <AdminLayout title="Impostazioni Economiche">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Impostazioni Economiche">
      <div className="space-y-4 pb-6" data-testid="economic-settings-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Impostazioni</h1>
            <p className="text-sm text-gray-500">Configura commissioni, tasse e deduzioni</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} data-testid="settings-reset-btn">
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save-btn">
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Salva
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Commissioni OTA */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Percent className="w-4 h-4 text-orange-500" />
                Commissioni OTA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  Booking.com
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_booking}
                    onChange={(e) => handleChange('commissione_booking', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div>
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  Airbnb
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_airbnb}
                    onChange={(e) => handleChange('commissione_airbnb', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div>
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  Expedia
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_expedia}
                    onChange={(e) => handleChange('commissione_expedia', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div>
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  Vrbo
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_vrbo}
                    onChange={(e) => handleChange('commissione_vrbo', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div>
                <Label className="text-sm flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  Diretta / Website
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_direct}
                    onChange={(e) => handleChange('commissione_direct', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Costi e Tasse */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-purple-500" />
                Costi e Tasse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Costo Transazione (Carte/Bonifici)</Label>
                <p className="text-xs text-gray-400 mb-1">Commissione gateway pagamento</p>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.costo_transazione}
                    onChange={(e) => handleChange('costo_transazione', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Label className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-red-500" />
                  Cedolare Secca
                </Label>
                <p className="text-xs text-gray-400 mb-1">Imposta sostitutiva sugli affitti brevi</p>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.cedolare_secca}
                    onChange={(e) => handleChange('cedolare_secca', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Label className="text-sm font-medium text-yellow-700">IVA % (Aliquota Entrate)</Label>
                <p className="text-xs text-gray-400 mb-1">Percentuale IVA applicata alle prenotazioni OTA in Gestione Entrate</p>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="22"
                    value={settings.iva_percentuale || 0}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      handleChange('iva_percentuale', val);
                      setSettings(prev => ({ ...prev, iva_percent: val }));
                    }}
                    className="w-24 border-yellow-300 focus:border-yellow-500"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                <p className="text-[10px] text-yellow-600 mt-1">⚠️ Le prenotazioni dirette sono esenti IVA</p>
              </div>

              <div className="pt-4 border-t">
                <Label className="text-sm">Altre Deduzioni</Label>
                <p className="text-xs text-gray-400 mb-1">Costi aggiuntivi fissi</p>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.altre_deduzioni}
                    onChange={(e) => handleChange('altre_deduzioni', e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Esempio Calcolo */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-white">
                <Calculator className="w-4 h-4 text-amber-400" />
                Esempio Calcolo
              </CardTitle>
              <p className="text-xs text-slate-400">Prenotazione Booking.com da €{exampleLordo}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Importo Lordo</span>
                <span className="font-bold text-lg">€{exampleLordo.toFixed(2)}</span>
              </div>

              <div className="border-t border-slate-700 pt-2 space-y-2">
                <div className="flex justify-between items-center text-orange-400">
                  <span className="text-sm">- Comm. OTA ({settings.commissione_booking}%)</span>
                  <span>-€{exampleCommOTA.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-purple-400">
                  <span className="text-sm">- Transazione ({settings.costo_transazione}%)</span>
                  <span>-€{exampleTransazione.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex justify-between items-center border-t border-slate-700 pt-2">
                <span className="text-slate-300">Subtotale Pre-Tax</span>
                <span className="font-medium">€{examplePreTax.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center text-red-400">
                <span className="text-sm">- Cedolare Secca ({settings.cedolare_secca}%)</span>
                <span>-€{exampleCedolare.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center border-t-2 border-amber-500 pt-3 mt-2">
                <span className="font-bold text-amber-400">NETTO FINALE</span>
                <span className="font-bold text-2xl text-emerald-400">€{exampleNetto.toFixed(2)}</span>
              </div>

              <div className="bg-slate-700/50 rounded-lg p-3 mt-4">
                <p className="text-xs text-slate-400">
                  <Info className="w-3 h-3 inline mr-1" />
                  La cedolare secca viene calcolata al netto delle commissioni OTA e costi di transazione.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sezione Tassa di Soggiorno */}
        <TouristTaxSettings token={token} />

        {/* Sezione Dati Aziendali per Ricevute Fiscali */}
        <Card className="border-blue-200">
          <CardHeader className="pb-2 bg-blue-50 rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-4 h-4 text-blue-600" />
              Dati Aziendali per Ricevute Fiscali
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">Questi dati verranno stampati sulle ricevute fiscali e fatture</p>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Colonna 1: Logo e Nome */}
              <div className="space-y-4">
                {/* Upload Logo */}
                <div>
                  <Label className="text-sm flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4 text-gray-500" />
                    Logo Aziendale
                  </Label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center bg-gray-50">
                    {settings.azienda_logo_url ? (
                      <div className="space-y-2">
                        <img 
                          src={settings.azienda_logo_url} 
                          alt="Logo" 
                          className="max-h-20 mx-auto object-contain"
                          data-testid="logo-preview"
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleRemoveLogo}
                          className="text-red-500 hover:text-red-700"
                          data-testid="logo-remove-btn"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Rimuovi
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 mx-auto text-gray-400" />
                        <p className="text-xs text-gray-500">PNG, JPG, WebP (max 2MB)</p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingLogo}
                          data-testid="logo-upload-btn"
                        >
                          {uploadingLogo ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2" />
                          ) : (
                            <Upload className="w-3 h-3 mr-1" />
                          )}
                          Carica Logo
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Nome Azienda */}
                <div>
                  <Label className="text-sm">Nome Azienda / Ragione Sociale *</Label>
                  <Input
                    value={settings.azienda_nome || ''}
                    onChange={(e) => handleChange('azienda_nome', e.target.value)}
                    placeholder="LA MAISONETTE DI PAESTUM"
                    className="mt-1"
                    data-testid="azienda-nome-input"
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Sottotitolo (opzionale)</Label>
                  <Input
                    value={settings.azienda_sottotitolo || ''}
                    onChange={(e) => handleChange('azienda_sottotitolo', e.target.value)}
                    placeholder="di Mario Rossi"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Colonna 2: Indirizzo */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm flex items-center gap-2">
                    <MapPin className="w-3 h-3 text-gray-500" />
                    Indirizzo *
                  </Label>
                  <Input
                    value={settings.azienda_indirizzo || ''}
                    onChange={(e) => handleChange('azienda_indirizzo', e.target.value)}
                    placeholder="VIA TAVERNELLE 44"
                    className="mt-1"
                    data-testid="azienda-indirizzo-input"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-sm">CAP</Label>
                    <Input
                      value={settings.azienda_cap || ''}
                      onChange={(e) => handleChange('azienda_cap', e.target.value)}
                      placeholder="84047"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm">Città</Label>
                    <Input
                      value={settings.azienda_citta || ''}
                      onChange={(e) => handleChange('azienda_citta', e.target.value)}
                      placeholder="CAPACCIO PAESTUM"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm">Provincia</Label>
                  <Input
                    value={settings.azienda_provincia || ''}
                    onChange={(e) => handleChange('azienda_provincia', e.target.value)}
                    placeholder="SA"
                    className="mt-1 w-20"
                    maxLength={2}
                  />
                </div>
                
                <div>
                  <Label className="text-sm flex items-center gap-2">
                    <User className="w-3 h-3 text-gray-500" />
                    Codice Fiscale *
                  </Label>
                  <Input
                    value={settings.azienda_codice_fiscale || ''}
                    onChange={(e) => handleChange('azienda_codice_fiscale', e.target.value.toUpperCase())}
                    placeholder="LSSNNT80S53A091J"
                    className="mt-1 uppercase"
                    maxLength={16}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Partita IVA</Label>
                  <Input
                    value={settings.azienda_partita_iva || ''}
                    onChange={(e) => handleChange('azienda_partita_iva', e.target.value.toUpperCase())}
                    placeholder="FRLLTA90L49D708S"
                    className="mt-1 uppercase"
                  />
                </div>
              </div>
              
              {/* Colonna 3: Contatti */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm flex items-center gap-2">
                    <Phone className="w-3 h-3 text-gray-500" />
                    Telefono
                  </Label>
                  <Input
                    value={settings.azienda_telefono || ''}
                    onChange={(e) => handleChange('azienda_telefono', e.target.value)}
                    placeholder="+39 3934957532"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm flex items-center gap-2">
                    <Mail className="w-3 h-3 text-gray-500" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={settings.azienda_email || ''}
                    onChange={(e) => handleChange('azienda_email', e.target.value)}
                    placeholder="info@lamaisonettepaestum.com"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm flex items-center gap-2">
                    <Globe className="w-3 h-3 text-gray-500" />
                    Sito Web
                  </Label>
                  <Input
                    value={settings.azienda_sito_web || ''}
                    onChange={(e) => handleChange('azienda_sito_web', e.target.value)}
                    placeholder="www.lamaisonettepaestum.com"
                    className="mt-1"
                  />
                </div>
                
                {/* Preview Card */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                  <p className="text-xs text-gray-500 mb-2">Anteprima intestazione ricevuta:</p>
                  <div className="text-xs">
                    {settings.azienda_logo_url && (
                      <img src={settings.azienda_logo_url} alt="Logo" className="h-8 mb-1 object-contain" />
                    )}
                    <p className="font-bold">{settings.azienda_nome || 'Nome Azienda'}</p>
                    {settings.azienda_sottotitolo && (
                      <p className="text-gray-600">{settings.azienda_sottotitolo}</p>
                    )}
                    <p className="text-gray-600">{settings.azienda_indirizzo || 'Indirizzo'}</p>
                    <p className="text-gray-600">
                      {settings.azienda_cap || 'CAP'} {settings.azienda_citta || 'Città'} 
                      {settings.azienda_provincia && ` (${settings.azienda_provincia})`}
                    </p>
                    <p className="text-gray-500 mt-1">
                      CF: {settings.azienda_codice_fiscale || '...'} 
                      {settings.azienda_partita_iva && ` | P.IVA: ${settings.azienda_partita_iva}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Template Email Fatture */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Mail className="w-5 h-5" />
              Template Email Fatture
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-4">
                <p className="font-medium mb-2">Variabili disponibili:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{numero}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{cliente_nome}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{totale}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{acconto}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{saldo}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{data_emissione}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{azienda_nome}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{azienda_telefono}}"}</code>
                  <code className="bg-blue-100 px-2 py-1 rounded">{"{{azienda_email}}"}</code>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Oggetto Email</Label>
                <Input
                  value={settings.email_fattura_oggetto || ''}
                  onChange={(e) => handleChange('email_fattura_oggetto', e.target.value)}
                  placeholder="Ricevuta Fiscale nr. {{numero}} - {{azienda_nome}}"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Corpo Email</Label>
                <textarea
                  value={settings.email_fattura_corpo || ''}
                  onChange={(e) => handleChange('email_fattura_corpo', e.target.value)}
                  rows={12}
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm font-mono resize-y"
                  placeholder="Gentile {{cliente_nome}},&#10;&#10;in allegato trova la ricevuta fiscale..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Suggerimento: usa <code className="bg-gray-100 px-1 rounded">{"{{#if acconto}}...{{/if}}"}</code> per testo condizionale
                </p>
              </div>

              {/* Preview Email */}
              <div className="border rounded-lg overflow-hidden mt-4">
                <div className="bg-gray-100 px-4 py-2 border-b">
                  <p className="text-xs text-gray-500">Anteprima Email</p>
                </div>
                <div className="p-4 bg-white">
                  <div className="border-b pb-2 mb-3">
                    <p className="text-sm"><strong>Oggetto:</strong> {(settings.email_fattura_oggetto || 'Ricevuta Fiscale nr. {{numero}}')
                      .replace('{{numero}}', '2026/0001')
                      .replace('{{azienda_nome}}', settings.azienda_nome || 'La Tua Azienda')
                    }</p>
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-gray-700">
                    {(settings.email_fattura_corpo || 'Gentile {{cliente_nome}}...')
                      .replace('{{cliente_nome}}', 'Mario Rossi')
                      .replace('{{numero}}', '2026/0001')
                      .replace('{{totale}}', '€ 500,00')
                      .replace('{{acconto}}', '€ 150,00')
                      .replace('{{saldo}}', '€ 350,00')
                      .replace('{{data_emissione}}', '01/08/2026')
                      .replace('{{azienda_nome}}', settings.azienda_nome || 'La Tua Azienda')
                      .replace('{{azienda_telefono}}', settings.azienda_telefono || '+39 000 0000000')
                      .replace('{{azienda_email}}', settings.azienda_email || 'info@azienda.com')
                      .replace(/\{\{#if acconto\}\}(.*?)\{\{\/if\}\}/gs, '$1')
                    }
                  </div>
                  <div className="mt-4 pt-3 border-t text-xs text-gray-500">
                    <p>📎 Allegato: Ricevuta_2026-0001.pdf</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Come vengono calcolate le trattenute</p>
                <ul className="list-disc list-inside space-y-1 text-amber-700">
                  <li><strong>Commissione OTA</strong>: Applicata sul lordo in base al canale di prenotazione</li>
                  <li><strong>Costo Transazione</strong>: Applicato sul lordo per pagamenti elettronici</li>
                  <li><strong>Cedolare Secca</strong>: Applicata sul netto pre-tasse (lordo - commissioni - transazioni)</li>
                </ul>
                <p className="mt-2 text-xs">
                  Formula: <code className="bg-amber-100 px-1 rounded">Netto = Lordo - Comm.OTA - Trans. - Cedolare</code>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
